import { useTranslation } from 'react-i18next';

import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { Card, CardContent, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';

import { ACTION_TARGETS, REWARD_TYPES } from '../../model/rewardTypes';
import type { ActionTarget } from '../../model/rewardTypes';
import type { PromotionAction } from '../../model/types';

export interface ActionCardProps {
  action: PromotionAction;
  index: number;
  hasProductSetCondition: boolean;
  disabled?: boolean;
  onChange: (action: PromotionAction) => void;
  onRemove: () => void;
}

/** One reward-action card in the ordered action list (plan-011 §8.3-D). */
export function ActionCard({
  action,
  index,
  hasProductSetCondition,
  disabled = false,
  onChange,
  onRemove,
}: ActionCardProps) {
  const { t } = useTranslation('promotions');
  const def = REWARD_TYPES.find((r) => r.type === action.type);
  const Icon = def?.icon;
  const setParam = (key: string, value: unknown) => onChange({ ...action, params: { ...action.params, [key]: value } });
  const target = (action.params.target as ActionTarget | undefined) ?? 'ORDER';
  const matchedDisabled = !hasProductSetCondition;

  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          {Icon ? <Icon fontSize="small" color="action" sx={{ mt: 1 }} /> : null}
          <Stack spacing={1.5} flex={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {index + 1}. {t(`promotions:rewardType.${action.type}`)}
            </Typography>

            {action.type === 'PERCENT_OFF' ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  size="small"
                  type="number"
                  label={t('actionEditor.percentLabel')}
                  value={action.params.percent ?? ''}
                  disabled={disabled}
                  onChange={(e) => setParam('percent', Number(e.target.value))}
                  slotProps={{ htmlInput: { min: 1, max: 100 } }}
                  sx={{ width: 140 }}
                />
                <TextField
                  select
                  size="small"
                  label={t('actionEditor.targetLabel')}
                  value={target}
                  disabled={disabled}
                  onChange={(e) => setParam('target', e.target.value)}
                  sx={{ minWidth: 220 }}
                >
                  {ACTION_TARGETS.map((opt) => (
                    <MenuItem key={opt} value={opt} disabled={opt === 'MATCHED_LINES' && matchedDisabled}>
                      {t(`actionEditor.target.${opt}`)}
                      {opt === 'MATCHED_LINES' && matchedDisabled
                        ? ` (${t('actionEditor.matchedLinesRequiresSet')})`
                        : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            ) : null}

            {action.type === 'AMOUNT_OFF' ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  size="small"
                  type="number"
                  label={t('actionEditor.amountLabel')}
                  value={action.params.amount ?? ''}
                  disabled={disabled}
                  onChange={(e) => setParam('amount', Number(e.target.value))}
                  helperText={t('actionEditor.amountOffHint')}
                  slotProps={{ htmlInput: { min: 0 } }}
                  sx={{ width: 160 }}
                />
                <TextField
                  select
                  size="small"
                  label={t('actionEditor.targetLabel')}
                  value={target}
                  disabled={disabled}
                  onChange={(e) => setParam('target', e.target.value)}
                  sx={{ minWidth: 220 }}
                >
                  {ACTION_TARGETS.map((opt) => (
                    <MenuItem key={opt} value={opt} disabled={opt === 'MATCHED_LINES' && matchedDisabled}>
                      {t(`actionEditor.target.${opt}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            ) : null}

            {action.type === 'FIXED_PRICE' ? (
              <Stack spacing={0.5}>
                <TextField
                  size="small"
                  type="number"
                  label={t('actionEditor.priceLabel')}
                  value={action.params.price ?? ''}
                  disabled={disabled}
                  error={matchedDisabled}
                  onChange={(e) => setParam('price', Number(e.target.value))}
                  slotProps={{ htmlInput: { min: 0 } }}
                  sx={{ width: 160 }}
                />
                {matchedDisabled ? (
                  <Typography variant="caption" color="error">
                    {t('actionEditor.fixedPriceRequiresSet')}
                  </Typography>
                ) : null}
              </Stack>
            ) : null}

            {action.type === 'FREE_SHIPPING' ? (
              <Typography variant="body2" color="text.secondary">
                {t('actionEditor.freeShippingBody')}
              </Typography>
            ) : null}

            {action.type === 'SHIPPING_PERCENT_OFF' ? (
              <TextField
                size="small"
                type="number"
                label={t('actionEditor.percentLabel')}
                value={action.params.percent ?? ''}
                disabled={disabled}
                onChange={(e) => setParam('percent', Number(e.target.value))}
                slotProps={{ htmlInput: { min: 1, max: 100 } }}
                sx={{ width: 140 }}
              />
            ) : null}
          </Stack>

          <Tooltip title={t('actionEditor.removeActionAria')}>
            <span>
              <IconButton
                size="small"
                aria-label={t('actionEditor.removeActionAria')}
                disabled={disabled}
                onClick={onRemove}
              >
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </CardContent>
    </Card>
  );
}
